import React, { type ReactNode, type ComponentType } from 'react';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { lazy } from '@atlassian/react-loosely-lazy';
import type { StatusValue } from '../common/types.tsx';
import type { ExpProps as Props } from './main.tsx';
import {
	StatusLozenge as IssueStatusFieldReadonly,
	StatusLozengeWithDropdownIndicator as IssueStatusFieldComponent,
} from './status-lozenge/index.tsx';

// eslint-disable-next-line @atlassian/eng-health/no-barrel-files/disallow-reexports
export { IssueStatusFieldReadonly, IssueStatusFieldComponent };
// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
export const AsyncLazyIssueStatusField = lazy<ComponentType<Props>>(
	() => import(/* webpackChunkName: "async-status-field" */ './main'),
);

export type ErrorProps = {
	value?: StatusValue;
	initialValue?: StatusValue;
};

export const AsyncIssueStatusField = (props: Props) => {
	const { initialValue, value } = props;
	const errorProps = { initialValue, value };
	return (
		<ErrorBoundary
			id="issue-field-status"
			packageName="jira-issue-field-status"
			render={() => <IssueStatusFieldComponent {...errorProps} />}
		>
			<Placeholder
				name="issue-status-field"
				fallback={<IssueStatusFieldComponent {...errorProps} />}
			>
				<AsyncLazyIssueStatusField {...props} />
			</Placeholder>
		</ErrorBoundary>
	);
};

export type IssueStatusFieldBoundaryProps = {
	children: ReactNode;
	fallback: ReactNode;
	packageName: string;
};

export const IssueStatusFieldBoundary = ({
	children,
	fallback,
	packageName,
}: IssueStatusFieldBoundaryProps) => (
	<ErrorBoundary id="issue-status-field" packageName={packageName} render={() => fallback}>
		<Placeholder name="issue-status-field-boundary" fallback={fallback}>
			{children}
		</Placeholder>
	</ErrorBoundary>
);
